import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponseEnvelope } from '@doctor-scheduling/shared/models';
import { AppointmentType } from '@doctor-scheduling/shared/constants';
import { API_BASE_URL } from './api-base-url.token';
import { AppointmentListItem, TimeSlot } from './appointment.api';

/** Public booking session context. */
export interface PublicBookingSession {
  sessionId: string;
  clinic: { id: string; name: string; slug: string };
  patient: {
    id: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string;
  } | null;
  doctors: Array<{
    id: string;
    specialization: string;
    user: { firstName: string; lastName: string };
  }>;
}

/** RSVP invite payload. */
export interface RsvpInvite {
  id: string;
  email: string;
  status: string;
  expiresAt: string;
  appointment: AppointmentListItem;
}

/** HTTP client for public booking and RSVP endpoints. */
@Injectable({
  providedIn: 'root',
})
export class PublicBookingApi {
  /** Angular HTTP client. */
  private readonly http = inject(HttpClient);
  /** NestJS API origin from the web app environment. */
  private readonly apiBaseUrl = inject(API_BASE_URL);
  /** Base URL for public routes under `/api/v1/public`. */
  private readonly baseUrl = `${this.apiBaseUrl}/api/v1/public`;

  /**
   * Requests a magic-link booking email.
   * @param data - Clinic slug and patient contact
   */
  startSession(data: {
    clinicSlug: string;
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  }): Observable<ApiResponseEnvelope<{ success: boolean; message: string }>> {
    return this.http.post<ApiResponseEnvelope<{ success: boolean; message: string }>>(
      `${this.baseUrl}/booking/session`,
      data
    );
  }

  /**
   * Resolves a booking session token.
   * @param token - Magic-link token
   */
  getSession(token: string): Observable<ApiResponseEnvelope<PublicBookingSession>> {
    return this.http.get<ApiResponseEnvelope<PublicBookingSession>>(
      `${this.baseUrl}/booking/session`,
      { params: { token } }
    );
  }

  /**
   * Lists slots for public booking.
   * @param token - Session token
   * @param doctorId - Doctor id
   * @param date - YYYY-MM-DD
   */
  getSlots(token: string, doctorId: string, date: string): Observable<ApiResponseEnvelope<TimeSlot[]>> {
    const params = new HttpParams()
      .set('token', token)
      .set('doctorId', doctorId)
      .set('date', date);
    return this.http.get<ApiResponseEnvelope<TimeSlot[]>>(`${this.baseUrl}/booking/slots`, { params });
  }

  /**
   * Books a slot via public session.
   * @param token - Session token
   * @param data - Slot payload
   */
  book(
    token: string,
    data: {
      doctorId: string;
      appointmentDate: string;
      startTime: string;
      endTime: string;
      type?: AppointmentType;
      reason?: string;
    }
  ): Observable<ApiResponseEnvelope<AppointmentListItem>> {
    return this.http.post<ApiResponseEnvelope<AppointmentListItem>>(
      `${this.baseUrl}/booking`,
      data,
      { params: { token } }
    );
  }

  /**
   * Reschedules via public session.
   * @param token - Session token
   * @param data - New slot + appointment id
   */
  reschedule(
    token: string,
    data: {
      appointmentId: string;
      doctorId: string;
      appointmentDate: string;
      startTime: string;
      endTime: string;
    }
  ): Observable<ApiResponseEnvelope<AppointmentListItem>> {
    return this.http.put<ApiResponseEnvelope<AppointmentListItem>>(
      `${this.baseUrl}/booking/reschedule`,
      data,
      { params: { token } }
    );
  }

  /**
   * Lists open appointments for the session patient.
   * @param token - Session token
   */
  listOpen(token: string): Observable<ApiResponseEnvelope<AppointmentListItem[]>> {
    return this.http.get<ApiResponseEnvelope<AppointmentListItem[]>>(
      `${this.baseUrl}/booking/appointments`,
      { params: { token } }
    );
  }

  /**
   * Loads RSVP invite by token.
   * @param token - Invite token
   */
  getRsvp(token: string): Observable<ApiResponseEnvelope<RsvpInvite>> {
    return this.http.get<ApiResponseEnvelope<RsvpInvite>>(
      `${this.baseUrl}/appointments/rsvp`,
      { params: { token } }
    );
  }

  /**
   * Accepts or declines an invite.
   * @param token - Invite token
   * @param action - accept | decline
   */
  respondRsvp(
    token: string,
    action: 'accept' | 'decline'
  ): Observable<ApiResponseEnvelope<{ success: boolean }>> {
    return this.http.post<ApiResponseEnvelope<{ success: boolean }>>(
      `${this.baseUrl}/appointments/rsvp`,
      { token, action }
    );
  }
}
